import { Database } from "@copro/api";

export type TopicDatabase = Database['public']['Tables']['topics']['Row'];

export type SubTopicDatabase = Database['public']['Tables']['sub_topics']['Row'];
export type SubTopicWithLastActivityDatabase = Database['public']['Views']['subjects_with_last_activity']['Row'];
export type SubTopicWithLastActivityWithIdDatabase = Omit<SubTopicWithLastActivityDatabase, 'id'> & { id: string };

export type PendingSubTopicWithPreview = SubTopicWithLastActivityWithIdDatabase & {
  preview_actions: SubjectHistoryFeed[];
  preview_tasks: SubjectTaskDatabase[];
  preview_pending_items: SubjectPendingItemDatabase[];
  preview_reminders: ReminderDatabase[];
  preview_pending_quotes: SubjectQuoteDatabase[];
};

export type EventDatabse = Database['public']['Tables']['events']['Row'];

export type DocumentTypeDatabase = Database['public']['Tables']['document_types']['Row'];
export type DocumentType = string;

export type DocumentDatabase = Database['public']['Tables']['documents']['Row'] & {
  document_types?: { id: string; name: string } | null;
  type: string | null;
};

export type DocumentMinimalData = {
  id: Database['public']['Tables']['documents']['Row']['id'];
  name: Database['public']['Tables']['documents']['Row']['name'] | null;
  file_url: Database['public']['Tables']['documents']['Row']['file_url'] | null;
  document_type_id?: Database['public']['Tables']['documents']['Row']['document_type_id'];
  type?: string | null;
  provider_id?: string | null;
  provider_name?: string | null;
  pendingFile?: File;
  isLink?: boolean;
}

export type ProviderDatabase = Database['public']['Tables']['providers']['Row'];
export type ProviderContactDatabase = Database['public']['Tables']['provider_contacts']['Row'];
export type ProviderTagDatabase = Database['public']['Tables']['provider_tags']['Row'];
export type ProviderTagAssignmentDatabase = Database['public']['Tables']['provider_tag_assignments']['Row'];
export type ProviderContractDatabase = Database['public']['Tables']['provider_contracts']['Row'] & {
  file_url: string | null;
};

export interface DisplayedProvider extends ProviderDatabase {
  contacts: ProviderContactDatabase[];
  contracts: ProviderContractDatabase[];
}

export type QuoteCompaisonGroupDatabase = Database['public']['Tables']['quote_comparison_groups']['Row'];
export type QuoteCompaisonColumnDatabase = Database['public']['Tables']['quote_comparison_columns']['Row'];
type QuoteDatabase = Database['public']['Tables']['quotes']['Row'];
export type QuoteCompaisonColumnType = Database["public"]["Enums"]["QuoteCompaisonColumnType"];

export interface Quote extends QuoteDatabase {
  id: string;
  provider: string;
  values: { [columnId: string]: string | number };
}

export type QuoteStatus = 'pending' | 'accepted' | 'refused';

export type DisplayedSubjectQuote = SubjectQuoteDatabase & {
  providers?: { id: string; name: string } | null;
  documents?: (DocumentDatabase & {
    document_types?: { id: string; name: string } | null;
  })[];
};

export function normalizeQuoteStatus(status: string | null | undefined): QuoteStatus {
  if (status === 'accepted' || status === 'approved') return 'accepted';
  if (status === 'refused' || status === 'rejected') return 'refused';
  return 'pending';
}

export type ExpenseCategoryDatabase = Database['public']['Tables']['expense_categories']['Row'];
export type ExpenseDatabase = Database['public']['Tables']['expenses']['Row'];

export type PrivateNotesDatabase = Database['public']['Tables']['private_notes']['Row'];

export type UserRoles = Database["public"]["Enums"]["user_role"];

export type SubjectHistoryFeed = Database['public']['Views']['subject_history_feed']['Row'];

export type SubjectTaskDatabase = Database['public']['Tables']['subject_tasks']['Row'];

export type SubjectPendingItemDatabase = Database['public']['Tables']['subject_pending_items']['Row'];

export type PendingItemWithProvider = SubjectPendingItemDatabase & {
  provider: { name: string } | null;
};

// Space-wide aggregated items joined with their parent sub-topic (id + name)
// so each row can link back to `/topics/{subject_id}`.
export type SubTopicRef = { id: string; name: string };

export type OpenTaskWithSubTopic = SubjectTaskDatabase & {
  sub_topics: SubTopicRef;
};

export type OpenPendingItemWithSubTopic = SubjectPendingItemDatabase & {
  sub_topics: SubTopicRef;
};

export type OpenQuoteWithSubTopic = SubjectQuoteDatabase & {
  sub_topics: SubTopicRef;
};

export type UpcomingEventWithSubTopic = EventDatabse & {
  sub_topics: SubTopicRef;
};

export type CalendarItemDatabase = Database['public']['Views']['calendar_items']['Row'];

export type CommunicationType = Database['public']['Enums']['communication_type'];

export type ReminderDatabase = Database['public']['Tables']['reminders']['Row'];
export type ReminderRecurringType = Database['public']['Enums']['recurrence_type'];

export type ActivityTimelineFeed = Database['public']['Views']['activity_timeline_feed']['Row'];
export type CommunicationDatabase = Database['public']['Tables']['communications']['Row'];
export type SubjectQuoteDatabase = Database['public']['Tables']['subject_quotes']['Row'];
export type IncidentDatabase = Database['public']['Tables']['incidents']['Row'];
export type IncidentStatus = Database['public']['Enums']['incident_status'];
